import type {DropdownProps} from '@Components/dropdown-v2';
import type {ListItemType8Storage} from '@Components/list/components/list-item-type-8';
import type {ListItemStorage} from '@Components/list/list.types';
import type {GenerateIdeasOriginator} from '@Libraries/ai-email-campaign-content-library';
import type {RefObject} from 'react';

export enum Tier {
  DEFAULT = 'default',
}

export enum BlacklistStatus {
  BLACKLISTED = 'blacklisted',
  WHITELISTED = 'whitelisted',
}

export enum LengthCategory {
  SHORT = 'short',
  MEDIUM = 'medium',
  LONG = 'long',
}

export enum Tone {
  NEUTRAL = 'neutral',
  PROFESSIONAL = 'professional',
  EXCITED = 'excited',
  CONFIDENT = 'confident',
  WITTY = 'witty',
  EMPOWERING = 'empowering',
  FUNNY = 'funny',
}

export enum HashtagUsage {
  USE_HASHTAGS = 'use_hashtags',
  NO_HASHTAGS = 'no_hashtags',
}

export interface PromptConfigOptions {
  lengthCategory: LengthCategory;
  hasHashtags: HashtagUsage;
  tone: Tone;
}

export type CommonListItemPropsType = Pick<ListItemType8Storage, 'type' | 'onClick' | 'className'>;

export type CommonDropDownPropsType = Pick<DropdownProps, 'popUpHasCustomWidth' | 'popupSpacingClass' | 'topOffset' | 'className' | 'alignment' | 'onClick'>;

export interface ErrorType {
  status: string;
  message: string;
  code: string;
  data: null;
}

export interface RecentCaptionType {
  captionid: string;
  generatedOn: string;
  caption: string;
  lengthCategory: LengthCategory;
  tone: Tone;
}

export const GENERATION_OPTIONS_COMPONENT_ID = 'generation-options';
export const NUMBER_OF_CAPTIONS_PER_GENERATION = 4;
export const GENERATOR_OPTIONS_DROPDOWN = 'generator-options-dropdown';

export interface InternationlizedPromptSuggestion {
  title: string;
  subtitle: string;
}

export enum PopupType {
  INTRO = 'intro',
  REMINDER = 'reminder',
}

export interface EmailSubject {
  subject: string;
  previewText: string;
}

export interface RewriteResponse {
  emailSubjects: EmailSubject[];
}

export interface EmailContent {
  idemail_campaign_content: number;
  iduser: number;
  content: string;
  prompt: string;
  options: {
    tone: string;
    useEmojis: string;
    purpose: string;
  };
  generated_on: string;
  content_type: string;
  content_group_key: string;
}

export interface EmailSubjectLineContentResponse {
  subject_line: EmailContent;
  preview_text: EmailContent;
  content_group_key: string;
}

export interface EmailSubjectAndPreviewResponse {
  subjectLine: EmailContent | null;
  previewText: EmailContent | null;
  contentGroupKey: string;
}

export enum Purpose {
  GenerateInterest = 'generate_interest',
  DriveAction = 'drive_action',
  AskAQuestion = 'ask_a_question',
}

export enum HasEmojis {
  Enabled = 'use_emojis',
  Disabled = 'no_emojis',
}

export enum EmailCampaignSubjectTone {
  Professional = 'professional',
  Emotional = 'emotional',
  Urgent = 'urgent',
  Funny = 'funny',
}

export interface EmailCampaignAIContentPromptOptions {
  [PromptBoxConfigOptionType.EMOJIS]: HasEmojis;
  [PromptBoxConfigOptionType.PURPOSE]: Purpose;
  [PromptBoxConfigOptionType.EMAIL_CAMPAIGN_SUBJECT_TONE]: EmailCampaignSubjectTone;
}

export type PromptBoxConfigOptionValue = Tone | LengthCategory | HashtagUsage | HasEmojis | EmailCampaignSubjectTone | Purpose;
export type PromptBoxConfigOptionValueEnum = typeof Tone | typeof LengthCategory | typeof HashtagUsage | typeof HasEmojis | typeof EmailCampaignSubjectTone | typeof Purpose;
export type ListAndListItems = Record<string, ListItemStorage[]>;
export type SelectedValueType = Record<string, PromptBoxConfigOptionValue>;

export interface OneOfPromptOption {
  key: PromptBoxConfigOptionType;
  value: PromptBoxConfigOptionValue;
}

export interface MultiContentAiGeneration {
  text: string;
  subtext: string;
}

export interface SingleContentAiGeneration {
  text: string;
  idPrompt?: number;
}

export type RecentlyGeneratedAiContent = {id: string; configOptions: SelectedValueType} & SingleContentAiGeneration & MultiContentAiGeneration;
export type AiGeneratedContent = SingleContentAiGeneration & MultiContentAiGeneration & RecentlyGeneratedAiContent;

export enum AiPromptBoxVariant {
  MULTICONTENT = 'multicontent',
  MONOCONTENT = 'monocontent',
  MINIATURE = 'miniature',
}

export interface PromptBoxTitles {
  main: string;
  recents: string;
  selected: string;
  primarySelectedContent: string;
  secondarySelectedContnt?: string;
}

export interface PromptBoxAlterationLoadingText {
  primaryAlterationLoadingMessage: string;
  secondaryAlterationLoadingMessage?: string;
}

/**
 * @description Mirrors Backend \AiTextGeneration\ErrorTypes enum
 */
export enum AiTextErrorTypes {
  PromptNotValidSentence = 'ac-prompt-not-valid-sentence',
  InvalidPrompt = 'invalid-prompt',
  LimitReached = 'daily-limit-reached',
  BadReq = 'bad-req',
  GeneralError = 'general-error',
  DbError = 'db-error',
  NoPrompt = 'no-prompt',
  NoCaption = 'no-caption',
  NoUserId = 'no-user-id',
  SpammyPrompt = 'spammy-prompt',
  PromptHasSuspiciousWords = 'prompt-has-suspicious-words',
  OpenAiException = 'open-ai-exception',
  UserBlacklisted = 'user-blacklist',
  OpenAiRateLimitHit = 'open-ai-rate-limit-exception',
  TooManyConcurrentRequests = 'too-many-concurrent-reqs',
  NoSubject = 'no-subject',
  NoTextField = 'no-text-field',
  LowCredits = 'low-credits',
  NoImage = 'no-image',
  ImageGenerationError = 'image-generation-error',
}

/**
 * When adding a new Option type, you need to add switch cases to
 * every method with the following comment: // Add a new switch case for PromptBoxConfigOptionType
 */
export enum PromptBoxConfigOptionType {
  LENGTH = 'length',
  TONE = 'tone',
  HASHTAGS = 'hashtags',
  PURPOSE = 'purpose',
  EMOJIS = 'emojis',
  EMAIL_CAMPAIGN_SUBJECT_TONE = 'emailCampaignSubjectTone',
}

interface AiTextPromptBoxPropsBase {
  onBack?: VoidFunction;
  titles?: PromptBoxTitles;
  alterationLoadingTexts?: PromptBoxAlterationLoadingText;
  isInMobileMode?: boolean;
  optionsToUse: PromptBoxConfigOptionType[];
  onContentSelected?: (selectedContent: AiGeneratedContent) => void;
  getRecents?: () => Promise<RecentlyGeneratedAiContent[]>;
  generateContent?: (prompt: string, selectedValues: SelectedValueType, originator: GenerateIdeasOriginator) => Promise<AiGeneratedContent[]>;
  useContent?: (prompt: string, selectedValues: SelectedValueType, content: AiGeneratedContent) => Promise<void>;
  reuseContent?: (recntlyGeneratedContentId: string) => Promise<void>;
  alterContent?: (prompt: string, selectedValues: SelectedValueType) => Promise<AiGeneratedContent>;
  mainPlaceholder?: string;
  /**
   * <AiTextPromptBoxContent [className] />
   */
  contentClassName?: string;
  /**
   * <AiTextPromptBoxContent [contentClassName] />
   */
  innerContentClassName?: string;
  /**
   * sectionsContainer = immediate parent of the header, content and footer
   */
  sectionsContainerClass?: string;
  /**
   * promptboxContainer = immediate parent of the section container
   */
  promptboxContainerClass?: string;
}

export interface MiniAiTextPromptBoxProps extends AiTextPromptBoxPropsBase {
  variant: AiPromptBoxVariant.MINIATURE;
  rewriteContent: (prompt: string, selectedValues: SelectedValueType) => Promise<void>;
  defaultPrompt?: string;
  defaultGenerationOptions?: SelectedValueType;
  onPromptChange?: (prompt: string) => void;
  onGenerationOptionChange?: (changedProperty: OneOfPromptOption) => void;
  ref?: RefObject<HTMLDivElement | null>;
  showBackButton?: boolean;
  isOpenedInPopOver?: boolean;

  onClose?(): void;
}

export interface NonMiniAiTextPromptBoxProps extends Required<AiTextPromptBoxPropsBase> {
  variant: Exclude<AiPromptBoxVariant, AiPromptBoxVariant.MINIATURE>;
  onPromptChange?: (prompt: string) => void;
  ref?: RefObject<HTMLDivElement | null>;
  internationalizedPromptSuggestions?: InternationlizedPromptSuggestion[];
  showBackButton?: boolean;
  isOpenedInPopOver?: boolean;

  onClose?(): void;
}

export type AiTextPromptBoxProps = MiniAiTextPromptBoxProps | NonMiniAiTextPromptBoxProps;

export function isMiniVariant(props: AiTextPromptBoxProps): props is MiniAiTextPromptBoxProps {
  return props.variant === AiPromptBoxVariant.MINIATURE;
}

export interface SocialMediaAutoCaptionsResponse {
  title: string;
  caption: string;
}

/**
 * hasMultiplePlatformCaptions drives whether the wizard stops mirroring one caption onto all accounts.
 */
export interface AutoCaptionGenerationResult {
  captionsByAccountId: Record<number, SocialMediaAutoCaptionsResponse>;
  hasMultiplePlatformCaptions: boolean;
}

export const defaultConfigForSocialMediaAutoGeneration: SelectedValueType = {
  [PromptBoxConfigOptionType.TONE]: Tone.NEUTRAL,
  [PromptBoxConfigOptionType.LENGTH]: LengthCategory.MEDIUM,
  [PromptBoxConfigOptionType.HASHTAGS]: HashtagUsage.USE_HASHTAGS,
};
